import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { MessageCircle, Send, Bot, User, TrendingUp, BarChart3, MapPin, DollarSign, Target, Zap, AlertCircle } from 'lucide-react';
import axios from 'axios';

const AIInsightsChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Initialize chat with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        id: `msg_${Date.now()}`,
        type: 'bot',
        content: 'Hello! I\'m your AI Media Analytics Assistant specialized in MENA region insights. I can help you analyze KPIs, performance metrics, ROI optimization, and provide actionable recommendations across your campaigns.\n\n🔍 **Ask me about:**\n• Budget performance & variance analysis\n• Channel effectiveness & ROI comparisons\n• Geographic performance insights\n• Optimization opportunities\n• Attribution modeling\n• Campaign diagnostics\n\nTry one of the quick insights below or ask me anything!',
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length]);

  const predefinedQuestions = [
    {
      id: 1,
      question: 'What is our overall ROI performance across MENA regions?',
      icon: <TrendingUp className="h-4 w-4" />,
      category: 'ROI Analysis'
    },
    {
      id: 2,
      question: 'Which channels are underperforming and why?',
      icon: <BarChart3 className="h-4 w-4" />,
      category: 'Performance'
    },
    {
      id: 3,
      question: 'Show me budget variance analysis by country',
      icon: <DollarSign className="h-4 w-4" />,
      category: 'Budget'
    },
    {
      id: 4,
      question: 'What are the top performing cities for outdoor advertising?',
      icon: <MapPin className="h-4 w-4" />,
      category: 'Geographic'
    },
    {
      id: 5,
      question: 'Identify opportunities for budget reallocation',
      icon: <Target className="h-4 w-4" />,
      category: 'Optimization'
    },
    {
      id: 6,
      question: 'Compare digital vs traditional media effectiveness',
      icon: <Zap className="h-4 w-4" />,
      category: 'Comparison'
    }
  ];

  // Send message to AI backend
  const sendToAI = async (messageText) => {
    try {
      const response = await axios.post(`${backendUrl}/api/chat/message`, {
        session_id: sessionId,
        message: messageText
      });
      
      return response.data.response;
    } catch (error) {
      console.error('AI Chat Error:', error);
      throw new Error('Failed to get AI response. Please try again.');
    }
  };

  const handleSendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim()) return;

    // Clear any previous errors
    setError(null);

    // Add user message
    const userMessage = {
      id: `msg_${Date.now()}`,
      type: 'user',
      content: messageText,
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Send to AI backend
      const aiResponseText = await sendToAI(messageText);
      
      const botMessage = {
        id: `msg_${Date.now()}_bot`,
        type: 'bot',
        content: aiResponseText,
        timestamp: new Date().toLocaleTimeString()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setError(error.message);
      
      // Add error message to chat
      const errorMessage = {
        id: `msg_${Date.now()}_error`,
        type: 'bot',
        content: `❌ **Connection Error**\n\nI'm having trouble connecting to my analytics engine. This could be due to:\n• Network connectivity issues\n• API service temporarily unavailable\n\nPlease try again in a moment. If the issue persists, try refreshing the page.`,
        timestamp: new Date().toLocaleTimeString(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handlePredefinedQuestion = (question) => {
    handleSendMessage(question);
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-20 z-[99999]" style={{ position: 'fixed', bottom: '24px', right: '80px', zIndex: 99999 }}>
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-200"
          style={{ width: '56px', height: '56px' }}
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
        {/* Notification badge */}
        <div className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 rounded-full flex items-center justify-center">
          <span className="text-xs font-bold text-white">AI</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-20 z-[99999]" style={{ position: 'fixed', bottom: '24px', right: '80px', zIndex: 99999 }}>
      <Card className="w-96 h-[600px] shadow-2xl border-2 border-blue-200">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bot className="h-6 w-6" />
              <div>
                <CardTitle className="text-lg">AI Media Insights</CardTitle>
                <p className="text-xs opacity-90">MENA Analytics Assistant</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              ×
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-0 flex flex-col h-[520px]">
          {/* Predefined Questions */}
          <div className="p-4 border-b bg-gray-50">
            <div className="text-sm font-medium text-gray-700 mb-2">Quick Insights</div>
            <div className="grid grid-cols-2 gap-2">
              {predefinedQuestions.slice(0, 4).map((q) => (
                <Button
                  key={q.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePredefinedQuestion(q.question)}
                  className="text-xs h-8 justify-start"
                >
                  {q.icon}
                  <span className="ml-1 truncate">{q.category}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${
                  message.type === 'user' 
                    ? 'bg-blue-600 text-white rounded-lg rounded-br-sm' 
                    : message.isError
                    ? 'bg-red-50 text-red-900 border border-red-200 rounded-lg rounded-bl-sm'
                    : 'bg-gray-100 text-gray-900 rounded-lg rounded-bl-sm'
                } p-3`}>
                  {message.isError && (
                    <div className="flex items-center mb-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mr-2" />
                      <span className="text-xs font-semibold text-red-600">Connection Issue</span>
                    </div>
                  )}
                  <div className="text-sm whitespace-pre-line">{message.content}</div>
                  <div className="text-xs opacity-70 mt-1 flex items-center justify-between">
                    <span>{message.timestamp}</span>
                    {message.type === 'bot' && !message.isError && (
                      <Badge className="bg-green-100 text-green-800 text-xs ml-2">
                        AI Generated
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg rounded-bl-sm p-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-white">
            <div className="flex space-x-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask about KPIs, ROI, regions..."
                className="flex-1 text-sm"
                disabled={isTyping}
              />
              <Button
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isTyping}
                size="sm"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIInsightsChatbot;